import classnames from 'classnames'
import React from 'react'

/**
 * The company meta row's icons as Font Awesome **solid** glyphs (ENG-1366). The outlined Material
 * icons they replace read too light against the company name they sit under.
 *
 * Solid is the header's *primary* weight, so only that top row uses these: the address / phone /
 * date items of {@link QuoteDocumentHeaderDesktop}, and the same three in the mobile sheet's
 * document section. Everything below — the Prepared for / Job Address / Prepared by columns, the
 * phone compact rows, the sheet's detail rows, and the contact menus — is secondary and stays on
 * the outlined Material icons.
 *
 * Each one takes the same `{ className }` contract as a Material `SvgIcon`, so the header's
 * `icon={SomeIcon}` props and `<SomeIcon className={classes.metaIcon} />` call sites are unchanged.
 * Size and colour therefore still come from the header's own classes (see
 * `quoteDocumentHeaderStyles`), which set 13-14px in Brand P50 (#55647c).
 *
 * The kit is a global script in every app that renders this header — `apps/app/index.html`,
 * `apps/customer-view/index.html` and `apps/admin/index.html` — so these render in the editor, in the
 * customer view, and in the PDF printed from the customer view. Font Awesome is already used in the
 * customer-facing document elsewhere (see `PaymentStatusBadge`), so this adds no new dependency.
 *
 * Solid only: the kit runs in webfont (`css`) mode and serves the 900 face alone, so a `fa-regular`
 * class silently renders solid (see `packages/@paintscout/ui/src/icons/SquarePlusIcon.tsx`). An
 * outlined tier therefore has to come from Material, not from Font Awesome.
 */
function faIcon(glyph: string, displayName: string) {
  const Icon = ({ className }: { className?: string }) => (
    <i className={classnames('fa-solid', glyph, className)} aria-hidden="true" />
  )
  Icon.displayName = displayName
  return Icon
}

export const LocationIcon = faIcon('fa-location-dot', 'LocationIcon')
export const PhoneIcon = faIcon('fa-phone', 'PhoneIcon')
export const EventIcon = faIcon('fa-calendar', 'EventIcon')
